import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ShieldCheck, UserRound, X } from 'lucide-react';
import { Button } from '@tark/shared/components';
import type {
  CompetitionConsent,
  CompetitionPairInviteResponse,
  PublicCompetition,
  PublicCompetitionDivision,
  WebUserProfile,
} from '@tark/shared/lib';
import { formatDivisionFormat } from '../../pages/competitions/publicCompetitionUtils';

export type CompetitionDialogMode = 'individual' | 'create-pair' | 'accept-pair' | 'claim';

export function CompetitionRegistrationDialog({
  open,
  mode,
  competition,
  division,
  profile,
  pairPreview,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  mode: CompetitionDialogMode;
  competition: PublicCompetition;
  division?: PublicCompetitionDivision;
  profile?: WebUserProfile;
  pairPreview?: CompetitionPairInviteResponse;
  isSubmitting: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (consent: CompetitionConsent) => Promise<void>;
}) {
  const [rulesAndResults, setRulesAndResults] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [communications, setCommunications] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setRulesAndResults(false);
    setPrivacy(false);
    setCommunications(false);
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSubmitting, onClose, open]);

  const profileName = useMemo(() => {
    const legalName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');
    return legalName || profile?.profileName || 'Your TARK profile';
  }, [profile]);
  const rating = profile?.ustaRank
    ? `Self-reported NTRP ${profile.ustaRank}`
    : profile?.utrRank
      ? `Self-reported UTR ${profile.utrRank}`
      : profile?.pickleballRank
        ? `Rating ${profile.pickleballRank}`
        : null;
  const allAccepted = rulesAndResults && privacy && communications;
  const copy = dialogCopy(mode, division, pairPreview);

  if (!open) {
    return null;
  }

  const consent: CompetitionConsent = {
    rulesAccepted: rulesAndResults,
    privacyAccepted: privacy,
    resultDisplayAccepted: rulesAndResults,
    communicationAccepted: communications,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-3 py-3 backdrop-blur-sm sm:items-center sm:px-6 sm:py-8"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) {
          onClose();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="competition-registration-title"
        className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase text-primary">{competition.name}</p>
            <h2 id="competition-registration-title" className="mt-1 text-xl font-bold text-text">
              {copy.title}
            </h2>
            <p className="mt-1 text-sm text-text-muted">{copy.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted hover:bg-surface-elevated hover:text-text disabled:opacity-50"
            aria-label="Close registration"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          {division ? (
            <div className="rounded-lg border border-border bg-surface-elevated px-4 py-3">
              <p className="font-semibold text-text">{division.name}</p>
              <p className="mt-1 text-sm text-text-muted">
                {[formatDivisionFormat(division.formatCode), division.levelLabel]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          ) : null}

          <div className="mt-4 flex items-center gap-3 rounded-lg border border-border px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <UserRound className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="font-semibold text-text">{profileName}</p>
              <p className="text-sm text-text-muted">
                {[profile?.zipCode ? `ZIP ${profile.zipCode}` : null, rating]
                  .filter(Boolean)
                  .join(' · ') || 'Identity from your TARK profile'}
              </p>
            </div>
          </div>

          <fieldset className="mt-5 space-y-3">
            <legend className="mb-3 flex items-center gap-2 text-sm font-semibold text-text">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Review and agree
            </legend>
            <ConsentRow checked={rulesAndResults} onChange={setRulesAndResults}>
              I accept the competition rules and understand that standings and results may be displayed.
            </ConsentRow>
            <ConsentRow checked={privacy} onChange={setPrivacy}>
              I accept the competition privacy notice.
            </ConsentRow>
            <ConsentRow checked={communications} onChange={setCommunications}>
              I agree to receive necessary competition updates from TARK and the organizer.
            </ConsentRow>
          </fieldset>

          <div className="mt-4 flex flex-wrap gap-3 text-xs font-semibold text-primary">
            {competition.rulesUrl ? (
              <a href={competition.rulesUrl} target="_blank" rel="noreferrer" className="hover:underline">
                Read rules
              </a>
            ) : null}
            <a href="/privacy" target="_blank" rel="noreferrer" className="hover:underline">
              Privacy notice
            </a>
          </div>

          {error ? (
            <div role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : null}
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(consent)}
            disabled={!allAccepted}
            isLoading={isSubmitting}
            leftIcon={<CheckCircle2 />}
          >
            {copy.confirmLabel}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function ConsentRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-3 text-sm text-text">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-border accent-[color:var(--color-primary)]"
      />
      <span className="leading-relaxed">{children}</span>
    </label>
  );
}

function dialogCopy(
  mode: CompetitionDialogMode,
  division?: PublicCompetitionDivision,
  pairPreview?: CompetitionPairInviteResponse
): { title: string; description: string; confirmLabel: string } {
  switch (mode) {
    case 'create-pair':
      return {
        title: 'Invite your doubles partner',
        description: 'TARK will create a private one-time link for this exact pair.',
        confirmLabel: 'Create partner link',
      };
    case 'accept-pair':
      return {
        title: 'Join this doubles team',
        description: pairPreview?.inviterDisplayName
          ? `${pairPreview.inviterDisplayName} invited you to play ${pairPreview.divisionName}.`
          : 'Accept this invitation to register as a fixed doubles team.',
        confirmLabel: 'Accept and register',
      };
    case 'claim':
      return {
        title: 'Link your registration',
        description: 'Connect the organizer-created registration to your verified TARK account.',
        confirmLabel: 'Claim registration',
      };
    default:
      return {
        title: `Register for ${division?.name || 'this division'}`,
        description: 'TARK uses your verified profile, so there is no second identity form.',
        confirmLabel: 'Submit registration',
      };
  }
}
